import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Stack,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import { formatCurrency } from '../../../helpers/formatCurrency';
import {
  AccountBalance as BankIcon,
  SwapHoriz as TransferIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as ExpenseIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
  Category as CategoryIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';

const FormSection = ({
  isBulkMode,
  isBulkEditMode,
  entryType,
  setEntryType,
  formData,
  handleInputChange,
  errors,
  allPositive,
  filteredCategories,
  isLoadingCategories,
  availableBankAccounts,
  isLoadingBanks,
  currentTransactions,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
        <Box
          sx={{
            backgroundColor: 'success.main',
            borderRadius: 2,
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DescriptionIcon sx={{ color: 'white', fontSize: 24 }} />
        </Box>
        <Box flex={1}>
          <Typography variant="h6" fontWeight="600" color="primary">
            Configuração do{isBulkMode ? 's' : ''} Lançamento{isBulkMode ? 's' : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Preencha os dados para criar {isBulkMode ? 'os lançamentos' : 'o lançamento'} no Vita
          </Typography>
        </Box>
        <Tooltip title="Configure categoria e descrição para criar os lançamentos correspondentes no sistema Vita">
          <InfoIcon color="action" fontSize="small" />
        </Tooltip>
      </Box>

      {/* Alerta para transações já conciliadas */}
      {errors.reconciled && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              fontSize: 24,
            },
          }}
        >
          {errors.reconciled}
        </Alert>
      )}

      <Stack spacing={3}>
        {/* Seletor de Tipo de Lançamento — oculto no modo 1:1 (isBulkEditMode) */}
        {!isBulkEditMode && (
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <Typography variant="subtitle1" fontWeight="600">
              Tipo de Lançamento *
            </Typography>
            <Tooltip title="Escolha se deseja criar um lançamento simples ou uma transferência entre contas">
              <HelpIcon fontSize="small" color="action" />
            </Tooltip>
          </Box>

          <RadioGroup
            value={entryType}
            onChange={(e) => setEntryType(e.target.value)}
            row
            sx={{ gap: 2 }}
          >
            <Paper
              elevation={entryType === 'regular' ? 3 : 0}
              sx={{
                flex: 1,
                p: 2,
                border: '2px solid',
                borderColor: entryType === 'regular' ? 'primary.main' : 'divider',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                background:
                  entryType === 'regular'
                    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
                    : 'transparent',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
              onClick={() => setEntryType('regular')}
            >
              <FormControlLabel
                value="regular"
                control={<Radio />}
                label={
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      {allPositive ? (
                        <IncomeIcon color={entryType === 'regular' ? 'primary' : 'action'} />
                      ) : (
                        <ExpenseIcon color={entryType === 'regular' ? 'primary' : 'action'} />
                      )}
                      <Typography variant="body1" fontWeight={entryType === 'regular' ? 600 : 500}>
                        {allPositive ? 'Entrada' : 'Saída'} Regular
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Lançamento único de {allPositive ? 'entrada' : 'saída'}
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, width: '100%' }}
              />
            </Paper>

            <Paper
              elevation={entryType === 'transfer' ? 3 : 0}
              sx={{
                flex: 1,
                p: 2,
                border: '2px solid',
                borderColor: entryType === 'transfer' ? 'primary.main' : 'divider',
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                background:
                  entryType === 'transfer'
                    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)'
                    : 'transparent',
                '&:hover': {
                  borderColor: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: 2,
                },
              }}
              onClick={() => setEntryType('transfer')}
            >
              <FormControlLabel
                value="transfer"
                control={<Radio />}
                label={
                  <Box>
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <TransferIcon color={entryType === 'transfer' ? 'primary' : 'action'} />
                      <Typography variant="body1" fontWeight={entryType === 'transfer' ? 600 : 500}>
                        Transferência
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Dois lançamentos vinculados entre contas
                    </Typography>
                  </Box>
                }
                sx={{ m: 0, width: '100%' }}
              />
            </Paper>
          </RadioGroup>
        </Box>
        )} {/* fim !isBulkEditMode — tipo de lançamento */}

        {/* Conta bancária de destino (apenas para transferências) */}
        {!isBulkEditMode && entryType === 'transfer' && (
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <BankIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight="600">
                Conta {allPositive ? 'de Origem' : 'de Destino'} *
              </Typography>
              <Tooltip
                title={
                  allPositive
                    ? 'A transferência sairá desta conta e entrará na conta do extrato'
                    : 'A transferência sairá da conta do extrato e entrará nesta conta'
                }
              >
                <HelpIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>

            <FormControl fullWidth error={!!errors.targetBankAccountId}>
              <InputLabel>Selecione a conta</InputLabel>
              <Select
                value={formData.targetBankAccountId}
                onChange={(e) => handleInputChange('targetBankAccountId', e.target.value)}
                label="Selecione a conta"
                disabled={isLoadingBanks}
                sx={{ borderRadius: 2 }}
              >
                {availableBankAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    <Box display="flex" alignItems="center" gap={1.5} width="100%">
                      <BankIcon fontSize="small" color="action" />
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {account.name || account.accountBankNumber || 'Sem nome'}
                        </Typography>
                        {account.accountBankNumber && account.name && (
                          <Typography variant="caption" color="text.secondary">
                            Conta: {account.accountBankNumber}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.targetBankAccountId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.targetBankAccountId}
                </Typography>
              )}
              {availableBankAccounts.length === 0 && !isLoadingBanks && (
                <Alert severity="warning" sx={{ mt: 1, borderRadius: 2 }}>
                  Nenhuma outra conta bancária disponível. A conta do extrato atual foi removida da
                  lista.
                </Alert>
              )}
            </FormControl>
          </Box>
        )}

        {/* Categoria — oculta no modo 1:1 (isBulkEditMode) */}
        {!isBulkEditMode && entryType === 'regular' && (
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <CategoryIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight="600">
                Categoria *
              </Typography>
              <Chip
                label={allPositive ? 'Entradas' : 'Saídas'}
                size="small"
                color={allPositive ? 'success' : 'error'}
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
              />
              <Tooltip title="Selecione a categoria correspondente ao tipo de lançamento">
                <HelpIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>

            <FormControl fullWidth error={!!errors.categoryId}>
              <InputLabel>Selecione a categoria</InputLabel>
              <Select
                value={formData.categoryId}
                onChange={(e) => handleInputChange('categoryId', e.target.value)}
                label="Selecione a categoria"
                disabled={isLoadingCategories}
                sx={{ borderRadius: 2 }}
              >
                {filteredCategories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {category.isInput ? (
                        <IncomeIcon fontSize="small" color="success" />
                      ) : (
                        <ExpenseIcon fontSize="small" color="error" />
                      )}
                      <Typography variant="body2">{category.name}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.categoryId && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.categoryId}
                </Typography>
              )}
              {filteredCategories.length === 0 && !isLoadingCategories && (
                <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
                  Nenhuma categoria disponível para o tipo de transação selecionado
                </Alert>
              )}
            </FormControl>
          </Box>
        )}

        {isBulkMode ? (
          // Modo bulk: descrições individuais para cada transação
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <DescriptionIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight="600">
                Descrições dos Lançamentos *
              </Typography>
              <Tooltip title="Personalize a descrição de cada lançamento individualmente">
                <HelpIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>

            {/* Campo para descrição padrão */}
            <TextField
              fullWidth
              label="Descrição Padrão (opcional)"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Digite uma descrição que será aplicada a todas as transações"
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              helperText="Se preenchido, será usado como base para todas as descrições"
              InputProps={{
                startAdornment: <DescriptionIcon sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 1.5, display: 'block', fontWeight: 500 }}
            >
              Personalize cada descrição individualmente:
            </Typography>

            <Box
              sx={{
                maxHeight: 320,
                overflow: 'auto',
                borderRadius: 2,
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'rgba(0,0,0,0.05)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px',
                  '&:hover': {
                    background: 'rgba(0,0,0,0.3)',
                  },
                },
              }}
            >
              <Stack spacing={1.5}>
                {currentTransactions.map((txn, index) => (
                  <Card
                    key={txn.transactionHash}
                    elevation={1}
                    sx={{
                      transition: 'all 0.2s ease',
                      border: '1px solid',
                      borderColor: errors[`individual_${txn.transactionHash}`]
                        ? 'error.main'
                        : 'divider',
                      '&:hover': {
                        boxShadow: 2,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={`#${index + 1}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                            <Typography variant="body2" fontWeight="600">
                              Lançamento {index + 1}
                            </Typography>
                          </Box>
                          <Chip
                            icon={txn.amount > 0 ? <IncomeIcon /> : <ExpenseIcon />}
                            label={formatCurrency(Math.abs(txn.amount))}
                            color={txn.amount > 0 ? 'success' : 'error'}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>

                        <Box
                          sx={{
                            p: 1,
                            backgroundColor: 'grey.50',
                            borderRadius: 1,
                            border: '1px dashed',
                            borderColor: 'grey.300',
                          }}
                        >
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            fontWeight="500"
                          >
                            Descrição Original:
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {txn.description}
                          </Typography>
                        </Box>

                        <TextField
                          fullWidth
                          label={`Descrição do Lançamento`}
                          value={formData.individualDescriptions[txn.transactionHash] || ''}
                          onChange={(e) =>
                            handleInputChange(
                              'individualDescription',
                              e.target.value,
                              txn.transactionHash
                            )
                          }
                          error={!!errors[`individual_${txn.transactionHash}`]}
                          helperText={
                            errors[`individual_${txn.transactionHash}`] ||
                            'Personalize ou deixe em branco para usar a descrição original'
                          }
                          placeholder={formData.description || txn.description}
                          multiline
                          rows={2}
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          InputProps={{
                            startAdornment: (
                              <DescriptionIcon
                                sx={{
                                  mr: 1,
                                  mt: 1,
                                  color: 'action.active',
                                  alignSelf: 'flex-start',
                                }}
                              />
                            ),
                          }}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          </Box>
        ) : (
          // Modo single: descrição única
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <DescriptionIcon fontSize="small" color="primary" />
              <Typography variant="subtitle2" fontWeight="600">
                Descrição do Lançamento
              </Typography>
              <Tooltip title="Descreva o lançamento de forma clara para facilitar futuras consultas">
                <HelpIcon fontSize="small" color="action" />
              </Tooltip>
            </Box>

            <TextField
              fullWidth
              label="Digite a descrição"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              error={!!errors.description}
              helperText={
                errors.description || 'Esta descrição aparecerá no lançamento criado no Vita'
              }
              multiline
              rows={3}
              placeholder="Digite uma descrição para o lançamento"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <DescriptionIcon
                    sx={{ mr: 1, mt: 1.5, color: 'action.active', alignSelf: 'flex-start' }}
                  />
                ),
              }}
            />
          </Box>
        )}
      </Stack>
    </Paper>
  );
};

export default FormSection;
